import { useCallback, useEffect, useRef } from 'react';
import { cloneTickOverrides } from '../../utils/tickOverrides';
import { cloneDocVariables } from './utils/docVariablesUtils';
import useHistoryStack from './hooks/useHistoryStack';
import useCoalescedCommit from './hooks/useCoalescedCommit';
import useDocVariablesCommit from './hooks/useDocVariablesCommit';
import useUndoRedo from './hooks/useUndoRedo';
import useBlockMutations from './hooks/useBlockMutations';
import useBlockMerge from './hooks/useBlockMerge';
import useCalloutHandlers from './hooks/useCalloutHandlers';
import useCanvasHandlers from './hooks/useCanvasHandlers';

/**
 * Identity-stable block CRUD handlers that read the CURRENT tree through
 * `blocksRef` (see STALENESS GUARD in DocEditor/index.js).
 *
 * Optional `docVariables` / `onDocVariablesChange` are snapshotted with the
 * block tree so in-doc Variables table edits (type/paste/add/delete/…) share
 * the same Ctrl+Z / Ctrl+Y stack. Mission tick overrides (Uncheck all) share
 * that stack when `onTickOverridesChange` is provided.
 */
export const useBlockHandlers = (
  blocks,
  onChange,
  {
    docVariables = null,
    onDocVariablesChange = null,
    tickOverrides = null,
    onTickOverridesChange = null,
  } = {}
) => {
  const blocksRef = useRef(blocks);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const docVariablesRef = useRef(cloneDocVariables(docVariables));
  const onDocVariablesChangeRef = useRef(onDocVariablesChange);
  onDocVariablesChangeRef.current = onDocVariablesChange;

  const tickOverridesRef = useRef(cloneTickOverrides(tickOverrides));
  const onTickOverridesChangeRef = useRef(onTickOverridesChange);
  onTickOverridesChangeRef.current = onTickOverridesChange;

  // Host-owned run-state (mission ticks) — keep the snapshot ref aligned
  // when props change. Local uncheck-all / undo write through applyTickOverrides.
  useEffect(() => {
    tickOverridesRef.current = cloneTickOverrides(tickOverrides);
  }, [tickOverrides]);

  const { historyRef, captureSnapshot, pushHistory } = useHistoryStack({
    blocksRef,
    docVariablesRef,
    tickOverridesRef,
  });

  const { commit } = useCoalescedCommit({
    blocks,
    blocksRef,
    onChangeRef,
    pushHistory,
  });

  const {
    applyDocVariables,
    commitDocVariables,
    commitDocVariablesAndBlocks,
    variablesHistoryEpoch,
  } = useDocVariablesCommit({
    docVariables,
    docVariablesRef,
    onDocVariablesChangeRef,
    historyRef,
    pushHistory,
    blocksRef,
    commit,
    tickOverridesRef,
  });

  const applyTickOverrides = useCallback((next) => {
    const cloned = cloneTickOverrides(next);
    tickOverridesRef.current = cloned;
    onTickOverridesChangeRef.current?.(cloned);
  }, []);

  const { undo, redo } = useUndoRedo({
    historyRef,
    captureSnapshot,
    commit,
    applyDocVariables,
    applyTickOverrides,
    docVariablesRef,
    onDocVariablesChangeRef,
    tickOverridesRef,
    onTickOverridesChangeRef,
  });

  const {
    handleUpdate,
    handleInsertAfter,
    handleUpdateAndInsert,
    handleDelete,
    handleNavigate,
    hasCheckedBoxes,
    uncheckAll,
  } = useBlockMutations({
    blocks,
    blocksRef,
    commit,
    tickOverridesRef,
    onTickOverridesChangeRef,
    pushHistory,
    applyTickOverrides,
  });

  const { handleMergeWithPrevious, handleMergeWithNext, handleEscapeContainer } =
    useBlockMerge({ blocksRef, commit });

  const { handleWrapInCallout, handleUnwrapCallout } = useCalloutHandlers({
    blocksRef,
    commit,
  });

  const { handleCanvasMouseDown, handleCanvasClick } = useCanvasHandlers({
    blocksRef,
    commit,
  });

  return {
    blocksRef,
    commit,
    commitDocVariables,
    commitDocVariablesAndBlocks,
    docVariablesRef,
    variablesHistoryEpoch,
    undo,
    redo,
    handleUpdate,
    handleInsertAfter,
    handleUpdateAndInsert,
    handleDelete,
    handleMergeWithPrevious,
    handleMergeWithNext,
    handleEscapeContainer,
    handleWrapInCallout,
    handleUnwrapCallout,
    handleNavigate,
    handleCanvasMouseDown,
    handleCanvasClick,
    hasCheckedBoxes,
    uncheckAll,
  };
};
